<template>
    <PageLoading :loading="loading">
        <!-- 错误页面 -->
        <ErrorPage v-if="hasError" :title="errorTitle" :message="errorMessage" />
        <div v-else class="inspection-report-page">
            <!-- 顶部Tab导航 -->
            <van-tabs v-model:active="activeTab" sticky class="report-tabs" :ellipsis="false" @click="loadReports">
                <van-tab v-for="tab in reportType" :key="tab.id" :title="tab.name" :name="tab.id">
                    <div class="reports-section">
                        <!-- 日期筛选 -->
                        <div class="date-filter" v-if="tab.is_time_filter">
                            <van-field v-model="dateRangeText" placeholder="请选择日期区间" readonly
                                @click="showCalendar = true">
                                <template #right-icon>
                                    <div class="field-icons">
                                        <van-icon v-if="dateRangeText" name="cross" @click.stop="clearDateRange"
                                            class="clear-icon" />
                                        <van-icon name="calendar-o" @click="showCalendar = true"
                                            class="calendar-icon" />
                                    </div>
                                </template>
                            </van-field>
                        </div>

                        <!-- 日期选择弹窗 -->
                        <van-calendar v-model:show="showCalendar" type="range" :min-date="minDate"
                            :allow-same-day="true" :max-date="maxDate" @confirm="onDateConfirm" color="#8B0000" />

                        <!-- 报告卡片网格 -->
                        <div class="reports-grid">
                            <div class="report-card" v-for="report in allReports" :key="report.content_url">
                                <div class="report-thumbnail" @click="handleReportClick(report)">
                                    <!-- PDF文件显示PDF图标 -->
                                    <div v-if="isPdfFile(report)" class="pdf-thumbnail">
                                        <van-image :src="require('../../assets/pdf.png')" class="image-pdf"
                                            fit="cover" />
                                    </div>
                                    <!-- 图片文件显示图片预览 -->
                                    <van-image v-else :src="report.content_url" class="image-thumbnail" fit="cover"
                                        loading="lazy" @click.stop="previewImage(report.content_url)" />
                                </div>
                                <div class="report-info">
                                    <div class="report-title">{{ report.name }}</div>
                                    <div class="report-date" v-if="report.effective_date">
                                        <van-icon :name="require('../../assets/icons/date.svg')" />
                                        <span>{{ report.effective_date }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </van-tab>
            </van-tabs>



            <!-- 图片预览 -->
            <van-image-preview v-model:show="showImagePreview" :images="[previewImageUrl]" :show-index="false"
                :closeable="true" />

            <!-- PDF预览弹窗 -->
            <van-dialog v-model:show="showPdfDialog" title="检测报告" :show-cancel-button="false" confirm-button-text="关闭"
                class="pdf-dialog" @close="onPdfDialogClose">
                <div class="pdf-container">
                    <!-- 简化的PDF预览区域 -->
                    <div v-if="currentPdfUrl" class="pdf-viewer">
                        <!-- 加载状态 -->
                        <div v-if="pdfLoading" class="pdf-loading">
                            <van-loading size="24px" color="#8B0000" />
                            <span>正在加载PDF文件...</span>
                        </div>
                    </div>
                </div>
            </van-dialog>

        </div>
    </PageLoading>
</template>

<script setup>
import { ref, onMounted } from 'vue'
import { setWxTitle } from '@/utils/wx'
import PageLoading from '@/components/PageLoading.vue'
import ErrorPage from '@/components/ErrorPage.vue'
import { WECHAT_APPID } from '../../../config/env'
import { post } from '@/utils/request'


onMounted(async () => {
    setWxTitle('检测报告')
    
    // 动态加载PDF.js
    await loadPdfJs()
    
    loadReports()
})

// 加载PDF.js库
const loadPdfJs = async () => {
    try {
        // 使用CDN加载PDF.js
        const script = document.createElement('script')
        script.src = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.5.207/build/pdf.min.js'
        script.onload = () => {
            // 设置PDF.js worker路径
            window.pdfjsLib.GlobalWorkerOptions.workerSrc = 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.5.207/build/pdf.worker.min.js'
            console.log('PDF.js加载成功')
        }
        script.onerror = () => {
            console.error('PDF.js加载失败')
        }
        document.head.appendChild(script)
    } catch (error) {
        console.error('PDF.js初始化失败:', error)
    }
}

// 加载状态
const loading = ref(false)
// 错误状态
const hasError = ref(false)
const errorTitle = ref('')
const errorMessage = ref('')

// 处理错误
const handleError = (title, message) => {
    hasError.value = true
    errorTitle.value = title
    errorMessage.value = message
    loading.value = false
}

// 当前激活的tab
const activeTab = ref('')

// 日期选择相关
const showCalendar = ref(false)
const startDate = ref('')
const endDate = ref('')
const dateRangeText = ref('')
const minDate = new Date(new Date().getFullYear() - 1, 0, 1)
const maxDate = new Date(new Date().getFullYear() + 1, 11, 31)

// 图片预览相关
const showImagePreview = ref(false)
const previewImageUrl = ref('')

// PDF预览相关
const showPdfDialog = ref(false)
const currentPdfUrl = ref('')
const pdfLoading = ref(false)
let pdfDoc = null

// 所有报告数据
const allReports = ref([])
const reportType = ref([])

// 加载报告数据
const loadReports = async () => {
    try {
        const params = {
            app_id: WECHAT_APPID,
        }
        console.log(activeTab.value)
        if (startDate.value) {
            params.start_date = startDate.value
            params.end_date = endDate.value
        }
        if (activeTab.value) {
            params.report_type_id = activeTab.value
        }
        const res = await post('/api/select/detection/reports', params)

        if (res.result && res.result.state === 'ok') {
            allReports.value = res.result.report_arr.flatMap(item => {
                // 对于每个报告项，将其与每个附件合并，生成一个扁平化的报告列表
                return item.attachments.map(attachment => {
                    return {
                        ...item, // 复制报告项的所有属性
                        ...attachment, // 复制附件的所有属性，如果与报告项有同名属性，附件的属性会覆盖报告项的
                    }
                })
            })
            reportType.value = res.result.report_type_arr || []
        } else {
            handleError('加载失败', res.result?.message || '接口异常')
        }
    } catch (err) {
        console.error('加载报告数据失败:', err)
        handleError('加载失败', '获取检测报告失败')
    } finally {
        loading.value = false
    }
}

// 判断是否为PDF文件
const isPdfFile = (report) => {
    return report.type === 'application/pdf'
}

// 格式化日期
const formatDate = (date) => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}-${month}-${day}`
}

// 处理报告点击
const handleReportClick = (report) => {
    if (isPdfFile(report)) {
        // 重置PDF状态
        currentPdfUrl.value = report.content_url
        showPdfDialog.value = true
        
        // 等待弹窗显示完成后再加载PDF
        setTimeout(() => {
            if (report.content_url) {
                loadPdf(report.content_url)
            }
        }, 300)
    }
}

// 日期确认处理函数
const onDateConfirm = async (values) => {
    const [start, end] = values
    startDate.value = formatDate(start)
    endDate.value = formatDate(end)
    dateRangeText.value = `${startDate.value} 至 ${endDate.value}`
    showCalendar.value = false
    loadReports()
}

// 清空日期区间
const clearDateRange = async () => {
    startDate.value = ''
    endDate.value = ''
    dateRangeText.value = ''
}

// 预览图片
const previewImage = (imageUrl) => {
    previewImageUrl.value = imageUrl
    showImagePreview.value = true
}

// 简化的PDF加载函数
const loadPdf = async (url) => {
    pdfLoading.value = true
    try {
        // 获取或创建页面容器
        let container = document.querySelector('.pdf-pages-container')
        
        // 如果容器不存在，创建一个新的
        if (!container) {
            container = document.createElement('div')
            container.className = 'pdf-pages-container'
            
            // 尝试多种方式查找父容器
            let pdfViewer = document.querySelector('.pdf-viewer')
            if (!pdfViewer) {
                pdfViewer = document.querySelector('.pdf-container')
            }
            pdfViewer.appendChild(container)
        } else {
            // 如果容器存在，清空内容
            container.innerHTML = ''
            console.log('使用现有的pdf-pages-container')
        }
        
        // 加载PDF文档
        const loadingTask = window.pdfjsLib.getDocument({
            url: url,
            cMapUrl: 'https://cdn.jsdelivr.net/npm/pdfjs-dist@2.5.207/cmaps/',
            cMapPacked: true,
        })
        
        pdfDoc = await loadingTask.promise
        const totalPages = pdfDoc.numPages
        console.log('开始渲染PDF页面，总页数:', totalPages)
        
        // 渲染所有页面
        for (let pageNum = 1; pageNum <= totalPages; pageNum++) {
            const canvas = document.createElement('canvas')
            canvas.id = `pdf-page-${pageNum}`
            
            container.appendChild(canvas)
            // console.log(`创建canvas: pdf-page-${pageNum}`)
            await renderPage(pageNum)
        }
        
        pdfLoading.value = false
        console.log('PDF加载成功:', url)
    } catch (error) {
        console.error('PDF加载失败:', error)
        pdfLoading.value = false
    }
}

const renderPage = async (pageNum) => {
    if (!pdfDoc) return;

    try {
        const page = await pdfDoc.getPage(pageNum)
        const canvas = document.getElementById(`pdf-page-${pageNum}`)
        const ctx = canvas.getContext('2d')

        // 获取设备像素比和容器宽度
        const dpr = window.devicePixelRatio || 2
        const containerWidth = document.querySelector('.pdf-container').clientWidth

        // 计算缩放比例，让PDF内容完全铺满容器宽度
        const viewport = page.getViewport({ scale: 1 })
        const scale = containerWidth / viewport.width // 直接铺满宽度，不加额外缩放

        // 创建高分辨率viewport
        const highResViewport = page.getViewport({ scale: scale * dpr })

        // 设置canvas尺寸为高分辨率尺寸
        canvas.width = highResViewport.width
        canvas.height = highResViewport.height

        // 使用CSS控制显示尺寸，确保铺满容器
        canvas.style.width = '100%'
        canvas.style.height = 'auto'

        // 使用更高质量的渲染设置
        await page.render({
            canvasContext: ctx,
            viewport: highResViewport,
            enableWebGL: true,
            renderInteractiveForms: false,
            quality: 2 // 提高渲染质量
        }).promise
    } catch (error) {
        console.error('PDF 渲染失败:', error)
    }
}

// 弹窗关闭时的处理
const onPdfDialogClose = () => {
    // 清空PDF内容
    // 清空容器内容
    const container = document.querySelector('.pdf-pages-container')
    if (container) {
        container.innerHTML = ''
    }
    
    // 重置PDF文档引用
    if (pdfDoc) {
        pdfDoc = null
    }
    
    // 重置加载状态
    pdfLoading.value = false
    // 重置URL
    currentPdfUrl.value = ''
    console.log('PDF弹窗关闭，内容已清空')
}

</script>

<style scoped>
.inspection-report-page {
    background: #fff;
    min-height: 100vh;
}

.report-tabs :deep(.van-tabs__nav) {
    gap: 7px;
}

:deep(.van-tabs--line .van-tabs__wrap) {
    background: #fff;
    padding: 14px 0;
}

:deep(.van-tab) {
    font-size: 12px;
}

.reports-section {
    padding: 0 14px 14px 14px;
}

/* 日期筛选样式 */
.date-filter {
    padding-bottom: 14px;
}

.date-filter :deep(.van-field) {
    background: #f8f9fa;
    border-radius: 8px;
}

.field-icons {
    display: flex;
    align-items: center;
    gap: 8px;
}

.clear-icon {
    color: #999;
    font-size: 16px;
}

.calendar-icon {
    color: #666;
    font-size: 16px;
}

/* 报告网格样式 */
.reports-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 14px;
}

.report-card {
    background: #fff;
    border-radius: 7px;
    box-shadow: 0px 1px 2px 0px rgba(0, 0, 0, 0.05);
    overflow: hidden;
}

.report-thumbnail {
    height: 112px;
    background: #F9FAFB;
    display: flex;
    align-items: center;
    justify-content: center;
    position: relative;
}

/* PDF缩略图样式 */
.pdf-thumbnail {
    width: 100%;
    height: 100%;
    display: flex;
    align-items: center;
    justify-content: center;
}

.image-pdf {
    width: 42px;
    height: 42px;
}

/* 图片缩略图样式 */
.image-thumbnail {
    width: 100%;
    height: 100%;
    border-radius: 0;
}

.image-thumbnail :deep(.van-image__img) {
    border-radius: 0;
}

/* PDF预览弹窗样式 */
:deep(.van-dialog) {
    top: 48%;
    width: 92%;
}

:deep(.van-dialog__content) {
    height: 75vh;
    overflow-y: auto;
    padding: 0;
}

.pdf-container {
    width: 100%;
    height: 100%;
}

.pdf-viewer {
    display: flex;
    flex-direction: column;
    height: 100%;
}

.pdf-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    min-height: 50vh;
}

.pdf-loading span {
    margin-top: 10px;
    color: var(--van-primary-color);
    font-size: 14px;
}

/* 报告信息样式 */
.report-info {
    padding: 10px;
}

.report-title {
    font-size: 12px;
    font-weight: 500;
    margin-bottom: 8px;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
}

.report-date {
    display: flex;
    align-items: center;
    gap: 4px;
    color: #6B7280;
    font-size: 10px;
}

.report-date .van-icon {
    font-size: 12px;
}

/* 图片预览样式优化 */
:deep(.van-image-preview__index) {
    display: none;
}

:deep(.van-image-preview__close-icon) {
    color: #fff;
    font-size: 20px;
}

:deep(.van-image-preview__image) {
    max-width: 100%;
    max-height: 100%;
}


</style>